import { orderByProductOrder } from '@provision/membership-plans-ui';
import { sortBy } from 'remeda';
import type { BundledActivationFlowConfig } from 'src/graphql';
import type { PartnerProductSummary } from 'src/graphql/entities/partner-product';
import { DEFAULT_BRANDING_VALUES } from 'src/pages/Settings/memberships/components/plans-tab/form-values';
import {
  type PlansTabFaqQuestionValues,
  type PlansTabFeatureValues,
  type PlansTabFormValues,
  type PlansTabPlanValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';

const parseAmountCents = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const centsToInputString = (amountCents: number): string => {
  if (!Number.isFinite(amountCents)) {
    return '';
  }

  const amount = amountCents / 100;

  if (Number.isInteger(amount)) {
    return String(amount);
  }

  return String(amount);
};

export const mapActivationFlowConfigToPlansTabFormValues = (
  config: BundledActivationFlowConfig,
  partnerProducts: PartnerProductSummary[] = [],
): PlansTabFormValues => {
  const products = Array.isArray(config.products) ? config.products : [];
  const features = Array.isArray(config.features) ? config.features : [];

  const mostPopularProductId = config.options?.mostPopularProductId ?? null;

  const productsById = products.reduce((acc: Record<string, (typeof products)[number]>, p) => {
    acc[p.id] = p;
    return acc;
  }, {});

  const orderedPartnerProducts = orderByProductOrder(
    partnerProducts,
    config.options?.productOrder,
    (product) => ({ id: product.partnerProductId, name: product.displayName }),
  );

  const plans: PlansTabPlanValues[] = orderedPartnerProducts.map((product, index) => {
    const configProduct = productsById[product.partnerProductId];
    const configMonthly = configProduct?.prices?.monthly;
    const configAnnual = configProduct?.prices?.annual;

    const monthlyAmountCents = parseAmountCents(
      configMonthly?.amountCents ?? product.prices.monthly?.amountCents,
    );
    const annualAmountCents = parseAmountCents(
      configAnnual?.amountCents ?? product.prices.annual?.amountCents,
    );

    return {
      id: product.partnerProductId,
      name: configProduct?.name ?? product.displayName,
      enabled: Boolean(configProduct),
      mostPopular: Boolean(
        mostPopularProductId && product.partnerProductId === mostPopularProductId,
      ),
      callForPricing: Boolean(configProduct?.callForPricing ?? product.callForPricing),
      monthlyPrice: monthlyAmountCents === null ? '' : centsToInputString(monthlyAmountCents),
      annualPrice: annualAmountCents === null ? '' : centsToInputString(annualAmountCents),
      sortOrder: index,
      platformSlaId: configProduct?.platformSlaId ?? product.platformSlaId ?? null,
    };
  });

  const planIdsByOrder = plans.map((plan) => plan.id);

  const featuresSorted = sortBy(features, [(feature) => feature.sortOrder ?? 0, 'asc']);

  const mappedFeatures: PlansTabFeatureValues[] = featuresSorted.map((feature, index) => {
    const includedPlanIds = planIdsByOrder.filter((planId) => {
      const product = productsById[planId];
      if (!product?.activeFeatures?.length) {
        return false;
      }

      return product.activeFeatures.some(
        (activeFeature) => activeFeature.membershipFeatureId === feature.membershipFeatureId,
      );
    });

    const overrideContentByPlanId: Record<string, string> = {};
    const overrideTooltipByPlanId: Record<string, string> = {};

    for (const planId of includedPlanIds) {
      const product = productsById[planId];
      const activeFeature = product?.activeFeatures?.find(
        (item) => item.membershipFeatureId === feature.membershipFeatureId,
      );

      const overrideContent = activeFeature?.overrideContent?.trim();
      const overrideTooltip = activeFeature?.overrideTooltip?.trim();

      if (overrideContent) {
        overrideContentByPlanId[planId] = overrideContent;
      }

      if (overrideTooltip) {
        overrideTooltipByPlanId[planId] = overrideTooltip;
      }
    }

    return {
      id: feature.membershipFeatureId,
      // Name is optional in the API
      name: feature.name?.trim() ?? '',
      defaultContent: feature.defaultContent ?? '',
      defaultTooltip: feature.defaultTooltip ?? '',
      includedPlanIds,
      overrideContentByPlanId,
      overrideTooltipByPlanId,
      sortOrder: feature.sortOrder ?? index + 1,
    };
  });

  const faqQuestionsRaw = config.options?.faqQuestions ?? [];
  const faqQuestions: PlansTabFaqQuestionValues[] = faqQuestionsRaw.map(
    (faqQuestion, index) => ({
      id: `faq-${index}`,
      question: faqQuestion.question ?? '',
      answer: faqQuestion.answer ?? '',
      sortOrder: index,
    }),
  );

  return {
    billingOptions: {
      monthlyEnabled: config.options?.showMonthlyPrices === true,
      annualEnabled: config.options?.showAnnualPrices === true,
    },
    plans,
    features: mappedFeatures,
    options: {
      showFaq: config.options?.showFaq ?? false,
      faqQuestions,
      showSkipPriceSelection: config.options?.showSkipPriceSelection ?? false,
      requireTosBeforePlans: config.options?.requireTosBeforePlans ?? false,
      setupCheckoutText: config.options?.setupCheckoutText ?? '',
      showContactSupport: config.options?.showContactSupport ?? false,
      contactSupportLink: config.options?.contactSupportLink ?? '',
      termsOfServiceUrl: config.options?.termsOfServiceUrl ?? '',
      privacyPolicyUrl: config.options?.privacyPolicyUrl ?? '',
      branding: {
        primaryColor:
          config.options?.branding?.primaryColor ?? DEFAULT_BRANDING_VALUES.primaryColor,
        secondaryColor:
          config.options?.branding?.secondaryColor ?? DEFAULT_BRANDING_VALUES.secondaryColor,
        bannerColor:
          config.options?.branding?.bannerColor ?? DEFAULT_BRANDING_VALUES.bannerColor,
        pageBlock1Color:
          config.options?.branding?.pageBlock1Color ?? DEFAULT_BRANDING_VALUES.pageBlock1Color,
        backgroundImageUrl: config.options?.branding?.backgroundImageUrl ?? '',
      },
    },
  };
};
